/** @jsxImportSource @emotion/react */

import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  useTheme,
  DateInput,
  Table,
  Icon,
  Pagination,
  UtilityButton,
  Option,
} from '@quickbit/qb-design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { QBLoader, MerchantSelect } from 'components';
import { RefundsSortOptions, searchRefunds } from './api';
import { ExportCsv } from './components';
const PAGE_SIZE = 16;

type CellKey = 'createdAt' | 'orderId' | 'refundAmount';

const getHeaders = (): {
  label: string;
  cellKey: CellKey;
  sort?: boolean;
}[] => [
  { label: 'Created At', cellKey: 'createdAt' },
  { label: 'Order ID', cellKey: 'orderId', sort: false },
  { label: 'Refund Amount', cellKey: 'refundAmount' },
];

const CELL_KEY_TO_SORT_FIELD: Record<CellKey, RefundsSortOptions['field']> = {
  createdAt: 'CREATED_AT',
  orderId: 'ORDER_ID',
  refundAmount: 'REFUND_AMOUNT',
};

interface RefundFilters {
  page: number;
  dateFilterRange: [Date | null, Date | null];
  sortOptions?: RefundsSortOptions;
  merchantIds: Option[];
}

const initialFilterState: RefundFilters = {
  page: 1,
  dateFilterRange: [null, null],
  merchantIds: [],
  sortOptions: undefined,
};

const getInitialFilterState = (params: URLSearchParams): RefundFilters => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const paramDateFilterRange = params.get('dateFilterRange')
    ? JSON.parse(params.get('dateFilterRange') as string)
    : undefined;
  return {
    page: Number(params.get('page')) || 1,
    dateFilterRange: paramDateFilterRange
      ? [
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          paramDateFilterRange[0] ? new Date(paramDateFilterRange[0]) : null,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          paramDateFilterRange[1] ? new Date(paramDateFilterRange[1]) : null,
        ]
      : [null, null],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    merchantIds: params.get('merchantIds')
      ? JSON.parse(params.get('merchantIds') as string)
      : [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    sortOptions: params.get('sortOptions')
      ? JSON.parse(params.get('sortOptions') as string)
      : undefined,
  };
};

export const MerchantRefunds = () => {
  const { spacing, palette } = useTheme();

  // State for filters persisted in query params
  const [params, setParams] = useSearchParams();

  const [filterState, setFilterState] = useState<RefundFilters>(
    getInitialFilterState(params)
  );
  const { page, dateFilterRange, sortOptions, merchantIds } = filterState;
  const setFilterStateFor = <K extends keyof RefundFilters>(
    filterName: K,
    isObject: boolean
  ) => {
    return (newValue: RefundFilters[K]) => {
      params.set(
        filterName,
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        isObject ? JSON.stringify(newValue) : String(newValue)
      );
      setParams(params);
      setFilterState((state) => ({ ...state, [filterName]: newValue }));
    };
  };
  const setPage = setFilterStateFor('page', false);
  const setDateFilterRange = setFilterStateFor('dateFilterRange', true);
  const setSortOptions = setFilterStateFor('sortOptions', true);

  const setMerchant = setFilterStateFor('merchantIds', true);

  const handleSort = useCallback(
    (cellKey: string, asc: boolean) => {
      setSortOptions({
        field: CELL_KEY_TO_SORT_FIELD[cellKey as CellKey],
        ascending: asc,
      });
    },
    [setSortOptions]
  );

  const handleResetFilters = () => {
    setParams(undefined);
    setFilterState(initialFilterState);
  };

  const [{ value, loading }, refreshOrders] = useAsyncFn(() => {
    return searchRefunds({
      pagination: { size: PAGE_SIZE, page },
      filters: {
        createdFrom: dateFilterRange[0],
        createdTo: dateFilterRange[1],
        merchantIds: merchantIds.map((m) => m.value),
      },
      sortOptions,
    });
  }, [dateFilterRange, page, sortOptions, merchantIds]);

  useEffect(() => {
    void refreshOrders();
  }, [refreshOrders]);

  const headers = getHeaders();
  const rows = useMemo(
    () =>
      value?.refunds.map((refund) => ({
        value: {
          createdAt: refund.createdAt.toLocaleString(),
          orderId: <TruncatedString text={refund.orderId} />,
          refundAmount: `${refund.refundAmount} ${refund.currency}`,
        },
      })),
    [value]
  );

  const totalRefundCount = useMemo(
    () =>
      value?.aggregatedData.reduce((accumulator, current) => {
        return accumulator + current.refundCount;
      }, 0) || 0,
    [value?.aggregatedData]
  );

  if (!value) return <QBLoader />;
  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateRows: 'auto auto auto 1fr auto',
          height: '100%',
        }}
      >
        <Header>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle">Refunds</Typography>
            <div
              css={{
                display: 'grid',
                gap: spacing.xxs,
                gridTemplateColumns: 'auto 1fr 1fr',
                alignItems: 'center',
              }}
            >
              <ExportCsv
                filters={{
                  createdFrom: dateFilterRange[0],
                  createdTo: dateFilterRange[1],
                  merchantIds: merchantIds.map((m) => m.value),
                }}
                sortOptions={sortOptions}
              />
            </div>
          </div>
        </Header>
        <div
          css={{
            marginTop: spacing.xxs,
            display: 'grid',
            gridTemplateColumns: '8fr 1fr',
            textWrap: 'nowrap',
          }}
        >
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: spacing.xxxs,
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <DateInput
              placeholder="All time"
              type="range"
              value={dateFilterRange}
              onChange={(e: [Date | null, Date | null]) => {
                setPage(1);
                setDateFilterRange(e);
              }}
              withTime
            />
            <MerchantSelect
              merchant={merchantIds}
              setMerchant={(m) => {
                setPage(1);
                setMerchant(m as Option[]);
              }}
              searchable={true}
              multiple={true}
            />
            <UtilityButton
              variant="text"
              text="Reset filters"
              onClick={handleResetFilters}
            />
            <UtilityButton
              variant="secondary"
              text="Refresh"
              onClick={() => void refreshOrders()}
              disabled={loading}
            />
            {loading && (
              <Icon
                name="spinner"
                className={{ svg: { color: palette.grey.default } }}
              />
            )}
          </div>
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Table headers={headers} rows={rows ?? []} onSort={handleSort} />
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Pagination
            currentPage={page}
            onChange={setPage}
            numberOfElements={totalRefundCount}
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>
    </>
  );
};
